import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import useSignupVerification from '../hooks/useSignupVerification';
import '../styles/pages/VerifyEmailPage.css';

export default function VerifyEmailPage() {
  const [code, setCode] = useState('');
  const flow = useSignupVerification();
  const showCode = !flow.verified && ['unverified', 'emailUnverified', 'verifying', 'resending'].includes(flow.phase);
  const status = {
    verifying: 'Verifying your email…', resending: 'Sending a new code…',
    signingIn: flow.verified ? 'Your email is verified. Signing you in…' : 'Signing in to retry account setup…',
    loadingAccount: 'Loading your account…',
  }[flow.phase];

  return (
    <main className="verify-page">
      <section className="verify-card" aria-labelledby="verify-heading">
        <h1 id="verify-heading">{!flow.hasEmail ? 'Continue your signup' : flow.phase === 'setupFailed' ? 'Account setup incomplete' : flow.verified ? 'Email verified' : 'Verify your email'}</h1>
        {flow.initializing ? <p role="status">Checking your account…</p> : !flow.hasEmail ? <>
          <p>Open this page from your signup to enter your verification code. If you have already created an account, log in to continue. You can request another code there if your email still needs verification.</p>
          <div className="verify-actions">
            <Button as="link" to="/login" size="lg">Continue to log in</Button>
            <Button as="link" to="/create-account" variant="secondary" size="lg">Create an account</Button>
          </div>
        </> : <>
          {showCode && <>
            <p>{flow.attributeVerification ? 'Verify the email address on your account:' : 'Enter the verification code sent to'} <strong>{flow.email}</strong>.</p>
            <form onSubmit={event => { event.preventDefault(); flow.verify(code); }}>
              <label className="verify-label" htmlFor="verification-code">Verification code</label>
              <input id="verification-code" value={code} onChange={event => setCode(event.target.value)}
                autoComplete="one-time-code" placeholder="Verification Code" className="verify-input" disabled={flow.busy} />
              <div className="verify-actions">
                <Button type="submit" size="lg" disabled={flow.busy}>{flow.phase === 'verifying' ? 'Verifying…' : 'Verify Email'}</Button>
                <Button variant="secondary" size="lg" onClick={flow.resend} disabled={flow.busy}>{flow.phase === 'resending' ? 'Sending…' : flow.attributeVerification ? 'Send verification code' : 'Resend Code'}</Button>
              </div>
            </form>
          </>}
          {!showCode && flow.verified && !flow.busy && <p>Your email is verified. Continue signing in to finish setting up your account.</p>}
          {status && <p role="status">{status}</p>}
          {flow.error && <p className="error-message" role="alert">{flow.error}</p>}
          {flow.info && <p className="info-message" role="status">{flow.info}</p>}
          <div className="verify-actions">
            {flow.phase === 'signInFailed' && <Button size="lg" onClick={flow.retrySignIn}>Retry sign-in</Button>}
            {flow.phase === 'setupFailed' && flow.canRetrySetup && <Button size="lg" onClick={flow.retrySetup}>Retry account setup</Button>}
            {['setupFailed', 'attributeSessionExpired'].includes(flow.phase) && <Button variant="secondary" size="lg" onClick={flow.continueToLogin}>{flow.phase === 'attributeSessionExpired' ? 'Log in to verify email' : 'Log in to retry account setup'}</Button>}
            {flow.phase === 'accountFailed' && <Button size="lg" onClick={flow.retryAccount}>Retry loading account</Button>}
            {(flow.verified || flow.phase === 'differentAccount') && flow.phase !== 'setupFailed' &&
              <Button variant="secondary" size="lg" onClick={flow.continueToLogin}>Continue to log in</Button>}
          </div>
        </>}
        <p className="verify-support">Need a hand? <Link to="/contact">Contact ViewFinders</Link></p>
      </section>
    </main>
  );
}
